import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, User, Layers, Download, Wallet } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import { PageHeader, Select, Input, Table, Th, Td, TrHover, Badge, LoadingSpinner, EmptyState, Modal } from '../components/UI';
import useSort from '../hooks/useSort';

// Três perguntas sobre a mesma base, em vez de três telas soltas: o filtro de
// período e titular é comum, e só o que muda de visão pra visão fica em cada aba.
const VISOES = [
  { id: 'faturas',  label: 'Faturas consolidadas', icon: FileText, desc: 'Todas as faturas por mês, titular e cartão' },
  { id: 'pessoais', label: 'Gastos pessoais',      icon: User,     desc: 'Por titular — Jaguar é o Rodrigo, Murilo é o Murilo' },
  { id: 'geral',    label: 'Consolidação geral',   icon: Layers,   desc: 'Todos os lançamentos, com filtro por categoria' },
  { id: 'fechamento', label: 'Fechamento', icon: Wallet, desc: 'Soma por centro: Pessoal, Operação X/J, Compras X/J, Taxa de embarque' },
];

// Piso do fechamento: antes de junho/2026 não existe operação Jaguar pra
// separar X de J. O servidor trava de todo jeito — isto aqui é pra o seletor
// não oferecer um mês que voltaria vazio.
// Piso da Consulta inteira. No Fechamento é conceitual (antes de junho/2026
// não existe operação Jaguar pra separar X de J, e o servidor trava de todo
// jeito); nas outras abas é o começo da operação nova — o que vem antes está
// indo pro banco de arquivo, então o seletor não oferece.
const MES_MIN_CONSULTA = '2026-06';

// Ordem fixa das colunas do fechamento — a do valor mudaria a cada consulta.
const CENTROS_ORDEM = ['PESSOAL', 'PESSOAL_X', 'PESSOAL_J', 'OPERACAO_X', 'OPERACAO_J',
  'OPERACAO_AGENCIA', 'TECNOLOGIA_XMTECH24',
  'COMPRAS_X', 'COMPRAS_J', 'TAXA_EMBARQUE_X', 'TAXA_EMBARQUE_J', 'EXCLUIDO'];
const CENTRO_COR = {
  // PESSOAL_X/J: gasto pessoal atribuído a uma operação, marca manual do
  // painel de conciliação (02/09/2026) — sem regra automática.
  PESSOAL: '#f59e0b', PESSOAL_X: '#d97706', PESSOAL_J: '#eab308',
  OPERACAO_X: '#94a3b8', OPERACAO_J: '#3b82f6',
  OPERACAO_AGENCIA: '#f472b6', TECNOLOGIA_XMTECH24: '#14b8a6',
  COMPRAS_X: '#64748b', COMPRAS_J: '#10b981',
  TAXA_EMBARQUE_X: '#64748b', TAXA_EMBARQUE_J: '#06b6d4', EXCLUIDO: '#475569',
};

// Centro cadastrado não tem cor própria: violeta, pra não se confundir com os de sistema.
const corCentro = (c) => CENTRO_COR[c] || '#a78bfa';

const mesAtual = () => new Date().toISOString().slice(0, 7);

function Kpi({ label, valor, sub, cor, onClick }) {
  return (
    <div onClick={onClick}
      title={onClick ? 'Clique para ver os lançamentos' : undefined}
      style={{
      flex: '1 1 170px', minWidth: 150, padding: 14, borderRadius: 10,
      background: colors.card, border: `1px solid ${onClick ? `${cor || colors.primary}55` : colors.border}`,
      cursor: onClick ? 'pointer' : 'default',
    }}>
      <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 21, fontWeight: 800, color: cor || colors.text, marginTop: 4 }}>{valor}</div>
      {sub && <div style={{ fontSize: 11, color: colors.textDim, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Baixar CSV: consulta serve pra levar número pra fora. Sem isto, todo mundo
// copia da tela na mão — e aí o número chega errado do outro lado.
function baixarCsv(nome, colunas, linhas) {
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [colunas.map(c => esc(c.label)).join(';')]
    .concat(linhas.map(l => colunas.map(c => esc(c.get(l))).join(';')))
    .join('\n');
  // BOM pro Excel abrir acentuação certa
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${nome}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function ConsultaPage() {
  const [visao, setVisao] = useState('faturas');
  const [mesDe, setMesDe] = useState(mesAtual());
  const [mesAte, setMesAte] = useState(mesAtual());
  const [titularId, setTitularId] = useState('');
  const [categoria, setCategoria] = useState('');
  const [origem, setOrigem] = useState('');
  const [pessoal, setPessoal] = useState('');
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState({ categorias: [], origens: [], titulares: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Banner do bônus de antecipação: descontos de parcela antecipada que o
  // banco credita na fatura (categoria Bonus / Antecipação). É o número que o
  // TechMiles vai somar no lucro bruto — aqui ele fica à vista no período.
  const [bonus, setBonus] = useState(null);
  // Drill-down do fechamento: número na tela sem como conferir linha a linha
  // não serve pra fechar mês — é o clique que transforma o card em prova.
  const [detalhe, setDetalhe] = useState(null); // { centro, categoria, titulo }
  const [detalheData, setDetalheData] = useState(null);
  const [detalheLoading, setDetalheLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/consulta/opcoes`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setOpcoes(d || {})).catch(() => {});
  }, []);

  const carregar = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (mesDe) p.set('mes_de', mesDe);
    if (mesAte) p.set('mes_ate', mesAte);
    if (titularId) p.set('titular_id', titularId);
    if (visao === 'geral') {
      if (categoria) p.set('categoria', categoria);
      if (origem) p.set('origem', origem);
      if (pessoal) p.set('pessoal', pessoal);
      if (busca) p.set('search', busca);
    }
    if (visao === 'pessoais' && categoria) p.set('categoria', categoria);
    fetch(`${API}/consulta/${visao}?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [visao, mesDe, mesAte, titularId, categoria, origem, pessoal, busca]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (mesDe) p.set('mes_de', mesDe);
    if (mesAte) p.set('mes_ate', mesAte);
    if (titularId) p.set('titular_id', titularId);
    fetch(`${API}/consulta/bonus-antecipacao?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setBonus(d && !d.error ? d : null)).catch(() => setBonus(null));
  }, [mesDe, mesAte, titularId]);

  const abrirDetalhe = useCallback((alvo) => {
    setDetalhe(alvo);
    setDetalheData(null);
    setDetalheLoading(true);
    // Mesmos filtros do resumo — se divergir, o modal mostra outro total e o
    // gestor deixa de confiar nos dois.
    const p = new URLSearchParams();
    if (mesDe) p.set('mes_de', mesDe);
    if (mesAte) p.set('mes_ate', mesAte);
    if (titularId) p.set('titular_id', titularId);
    p.set('centro', alvo.centro);
    if (alvo.categoria) p.set('categoria', alvo.categoria);
    fetch(`${API}/consulta/fechamento/lancamentos?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(setDetalheData).catch(() => setDetalheData(null))
      .finally(() => setDetalheLoading(false));
  }, [mesDe, mesAte, titularId]);

  const linhas = useMemo(() => {
    if (!data) return [];
    if (visao === 'fechamento') return []; // fechamento não tem lista — só agregados
    if (visao === 'faturas') return data.faturas || [];
    return data.lancamentos || [];
  }, [data, visao]);
  const { sorted, sortKey, sortDir, toggleSort } = useSort(linhas, visao === 'faturas' ? 'mes_referencia' : 'data_compra', 'desc');
  const sortState = { sortKey, sortDir };

  const R = data?.resumo || {};

  return (
    <div>
      <PageHeader title="Consulta" subtitle={VISOES.find(v => v.id === visao)?.desc} />

      {/* Abas */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {VISOES.map(v => {
          const Icon = v.icon; const ativa = visao === v.id;
          return (
            <button key={v.id} onClick={() => {
                setVisao(v.id); setCategoria(''); setOrigem(''); setPessoal(''); setBusca('');
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 9,
                border: `1px solid ${ativa ? colors.primary : colors.border}`,
                background: ativa ? `${colors.primary}22` : colors.card,
                color: ativa ? colors.primary : colors.textMuted,
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}>
              <Icon size={15} /> {v.label}
            </button>
          );
        })}
      </div>

      {/* Filtros comuns + os da visão */}
      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
        padding: 12, marginBottom: 16, background: colors.card,
        border: `1px solid ${colors.border}`, borderRadius: 10,
      }}>
        <span style={{ fontSize: 12, color: colors.textMuted }}>Período</span>
        <Input type="month" value={mesDe} onChange={e => setMesDe(e.target.value)}
          min={MES_MIN_CONSULTA}
          style={{ width: 'auto', colorScheme: 'dark' }} />
        <span style={{ fontSize: 12, color: colors.textDim }}>até</span>
        <Input type="month" value={mesAte} onChange={e => setMesAte(e.target.value)}
          min={MES_MIN_CONSULTA}
          style={{ width: 'auto', colorScheme: 'dark' }} />

        <Select value={titularId} onChange={e => setTitularId(e.target.value)} style={{ width: 'auto', minWidth: 190 }}>
          <option value="">Todos os titulares ({(opcoes.titulares || []).length})</option>
          {(opcoes.titulares || []).map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </Select>

        {(visao === 'geral' || visao === 'pessoais') && (
          <Select value={categoria} onChange={e => setCategoria(e.target.value)} style={{ width: 'auto', minWidth: 190 }}>
            <option value="">Todas as categorias</option>
            {(opcoes.categorias || []).map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        )}
        {visao === 'fechamento' && (<>
          {/* "Fora da soma" saiu daqui: era o mesmo número do card EXCLUÍDO
              logo abaixo, com nome diferente — dois rótulos pro mesmo valor
              fazem o leitor procurar a diferença que não existe. */}
          <Kpi label="Despesas no período" valor={fmt(R.total)} sub={`${R.qtd ?? 0} lançamento(s)`} />
          {(R.espelho_qtd > 0) && (
            <Kpi label="Espelho (não somado)" valor={fmt(R.espelho_total)} cor={colors.textDim}
              sub={`${R.espelho_qtd} lançamento(s) — outro lado da mesma compra (TechMiles × fatura)`} />
          )}
          <Kpi label="Marco X → J" valor={data?.marco || '-'} cor={colors.info} sub="início da operação Jaguar" />
        </>)}
        {visao === 'geral' && (<>
          <Select value={origem} onChange={e => setOrigem(e.target.value)} style={{ width: 'auto', minWidth: 150 }}>
            <option value="">Todas as origens</option>
            {(opcoes.origens || []).map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
          <Select value={pessoal} onChange={e => setPessoal(e.target.value)} style={{ width: 'auto', minWidth: 150 }}>
            <option value="">Pessoal e operação</option>
            <option value="sim">Só pessoais</option>
            <option value="nao">Só da operação</option>
          </Select>
          <Input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar descrição..." style={{ width: 190 }} />
        </>)}

        <button onClick={() => baixarCsv(
          `consulta-${visao}-${mesDe}_${mesAte}`,
          visao === 'fechamento'
            ? [
                { label: 'Centro', get: l => l.centro }, { label: 'Categoria', get: l => l.categoria },
                { label: 'Lançamentos', get: l => l.qtd }, { label: 'Total', get: l => l.total },
              ]
            : visao === 'faturas'
            ? [
                { label: 'Mês', get: l => l.mes_referencia }, { label: 'Titular', get: l => l.titular_nome },
                { label: 'Cartão', get: l => l.nome_cartao }, { label: 'Banco', get: l => l.banco_nome },
                { label: 'Vencimento', get: l => l.data_vencimento }, { label: 'Valor', get: l => l.valor_total },
                { label: 'Status', get: l => l.status }, { label: 'Pago', get: l => l.valor_pago },
              ]
            : [
                { label: 'Data', get: l => l.data_compra }, { label: 'Mês', get: l => l.mes_referencia },
                { label: 'Titular', get: l => l.titular_nome }, { label: 'Cartão', get: l => l.cartao_nome || l.nome_cartao },
                { label: 'Descrição', get: l => l.descricao }, { label: 'Categoria', get: l => l.categoria },
                { label: 'Origem', get: l => l.origem }, { label: 'Valor', get: l => l.valor },
              ],
          visao === 'fechamento' ? (data?.por_categoria || []) : sorted)}
          style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, border: `1px solid ${colors.info}55`,
            background: `${colors.info}18`, color: colors.info, fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}>
          <Download size={14} /> CSV
        </button>
      </div>

      {/* KPIs da visão */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {visao === 'faturas' && (<>
          <Kpi label="Faturas" valor={R.qtd_faturas ?? 0} />
          <Kpi label="Total" valor={fmt(R.total)} cor={colors.text} />
          <Kpi label="Pago" valor={fmt(R.pago)} cor={colors.success} />
          <Kpi label="Em aberto" valor={fmt(R.em_aberto)} cor={colors.danger} />
          <Kpi label="Sem OCR" valor={R.sem_ocr ?? 0} cor={R.sem_ocr ? colors.warning : colors.textMuted}
            sub="fatura carregada e não processada" />
        </>)}
        {visao === 'pessoais' && (<>
          <Kpi label="Lançamentos" valor={R.qtd ?? 0} />
          <Kpi label="Total pessoal" valor={fmt(R.total)} cor={colors.warning} />
        </>)}
        {visao === 'fechamento' && (<>
          {/* "Fora da soma" saiu daqui: era o mesmo número do card EXCLUÍDO
              logo abaixo, com nome diferente — dois rótulos pro mesmo valor
              fazem o leitor procurar a diferença que não existe. */}
          <Kpi label="Despesas no período" valor={fmt(R.total)} sub={`${R.qtd ?? 0} lançamento(s)`} />
          {(R.espelho_qtd > 0) && (
            <Kpi label="Espelho (não somado)" valor={fmt(R.espelho_total)} cor={colors.textDim}
              sub={`${R.espelho_qtd} lançamento(s) — outro lado da mesma compra (TechMiles × fatura)`} />
          )}
          <Kpi label="Marco X → J" valor={data?.marco || '-'} cor={colors.info} sub="início da operação Jaguar" />
        </>)}
        {visao === 'geral' && (<>
          <Kpi label="Lançamentos" valor={R.qtd ?? 0} sub={R.truncado ? `mostrando ${R.exibindo} na lista` : undefined} />
          <Kpi label="Total" valor={fmt(R.total)} />
          <Kpi label="Pessoais" valor={fmt(R.total_pessoal)} sub={`${R.qtd_pessoal ?? 0} lançamento(s)`} cor={colors.warning} />
        </>)}
      </div>

      {/* Bônus de antecipação do período — visível em todas as abas */}
      {bonus && bonus.total > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          padding: '10px 14px', marginBottom: 16, borderRadius: 10,
          background: `${colors.success}12`, border: `1px solid ${colors.success}44`,
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: colors.success, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Bônus antecipação no período
          </span>
          <span style={{ fontSize: 18, fontWeight: 800, color: colors.success }}>{fmt(bonus.total)}</span>
          <span style={{ fontSize: 11, color: colors.textMuted }}>
            {bonus.qtd} desconto(s) de parcela antecipada creditados na fatura
          </span>
        </div>
      )}

      {/* Quebras — é onde a consulta responde sem precisar ler a lista inteira */}
      {visao === 'fechamento' && data?.mes_minimo && mesDe < data.mes_minimo && (
        <div style={{
          marginBottom: 14, padding: '9px 13px', borderRadius: 8, fontSize: 12,
          background: `${colors.info}14`, border: `1px solid ${colors.info}44`, color: colors.info,
        }}>
          O fechamento começa em <strong>{data.mes_minimo}</strong> — antes disso não existe
          operação Jaguar pra separar X de J. O período foi ajustado.
        </div>
      )}

      {visao !== 'fechamento' && !loading && data && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
          {(visao === 'faturas' ? [
            { titulo: 'Por titular', itens: data.por_titular, chave: 'titular', valor: 'total', qtd: 'qtd' },
            { titulo: 'Por mês', itens: data.por_mes, chave: 'mes', valor: 'total', qtd: 'qtd' },
          ] : visao === 'pessoais' ? [
            { titulo: 'Por titular', itens: data.por_titular, chave: 'titular', valor: 'total', qtd: 'qtd' },
            { titulo: 'Por cartão', itens: data.por_cartao, chave: 'cartao', valor: 'total', qtd: 'qtd' },
          ] : [
            { titulo: 'Por categoria', itens: data.por_categoria, chave: 'categoria', valor: 'total', qtd: 'qtd' },
          ]).filter(b => (b.itens || []).length).map(b => (
            <div key={b.titulo} style={{
              flex: '1 1 300px', minWidth: 260, maxHeight: 260, overflowY: 'auto',
              background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 12,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, marginBottom: 8 }}>{b.titulo}</div>
              {(b.itens || []).slice(0, 40).map((i, ix) => (
                <div key={ix} style={{
                  display: 'flex', justifyContent: 'space-between', gap: 10,
                  fontSize: 12, padding: '4px 0', borderBottom: `1px solid ${colors.border}33`,
                }}>
                  <span style={{ color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {i[b.chave]} <span style={{ color: colors.textDim }}>({i[b.qtd]})</span>
                  </span>
                  <strong style={{ color: colors.text, whiteSpace: 'nowrap' }}>{fmt(i[b.valor])}</strong>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {loading ? <LoadingSpinner /> : visao === 'fechamento' ? (!data?.centros?.length ? (
        <EmptyState message="Nada encontrado com esses filtros" icon={Wallet} />
      ) : (() => {
        const meses = [...new Set((data.por_mes || []).map(m => m.mes))].sort((a, b) => b.localeCompare(a));
        // Os de sistema na ordem fixa; os CADASTRADOS (06/10/2026 — ex.:
        // Operação Agência) depois, na ordem em que vieram. Antes a lista era só
        // a fixa, e um centro novo sumia do relatório sem erro nenhum — e o
        // dinheiro dele, dos totais da tela.
        const centrosAtivos = [
          ...CENTROS_ORDEM.filter(c => (data.centros || []).some(x => x.centro === c)),
          ...(data.centros || []).map(x => x.centro).filter(c => c && !CENTROS_ORDEM.includes(c)),
        ];
        const celula = {};
        (data.por_mes || []).forEach(m => { celula[`${m.mes}|${m.centro}`] = m.total; });
        const totalCentro = {};
        (data.centros || []).forEach(c => { totalCentro[c.centro] = c; });
        const rotulo = (c) => totalCentro[c]?.rotulo || c;
        const catsDe = (c) => (data.por_categoria || []).filter(x => x.centro === c);
        return (<>
          {/* Cards por centro */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
            {centrosAtivos.map(c => (
              <Kpi key={c} label={rotulo(c)} valor={fmt(totalCentro[c]?.total)}
                sub={`${totalCentro[c]?.qtd ?? 0} lançamento(s)`} cor={corCentro(c)}
                onClick={() => abrirDetalhe({ centro: c, titulo: rotulo(c) })} />
            ))}
          </div>

          {/* Mês × centro — a tabela do fechamento */}
          <Table>
            <thead><tr>
              <Th>Mês</Th>
              {centrosAtivos.map(c => <Th key={c} align="right">{rotulo(c)}</Th>)}
            </tr></thead>
            <tbody>
              {meses.map(m => (
                <TrHover key={m}>
                  <Td style={{ fontWeight: 700 }}>{m}</Td>
                  {centrosAtivos.map(c => (
                    <Td key={c} align="right" style={{ color: celula[`${m}|${c}`] ? colors.text : colors.textDim }}>
                      {celula[`${m}|${c}`] ? fmt(celula[`${m}|${c}`]) : '—'}
                    </Td>
                  ))}
                </TrHover>
              ))}
              <tr>
                <Td style={{ fontWeight: 800 }}>Total</Td>
                {centrosAtivos.map(c => (
                  <Td key={c} align="right" style={{ fontWeight: 800, color: corCentro(c) }}>
                    {fmt(totalCentro[c]?.total)}
                  </Td>
                ))}
              </tr>
            </tbody>
          </Table>

          {/* Categoria dentro de cada centro: é como se confere se a regra acertou */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 18 }}>
            {centrosAtivos.map(c => (
              <div key={c} style={{
                flex: '1 1 280px', minWidth: 240, maxHeight: 240, overflowY: 'auto',
                background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 12,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: corCentro(c), marginBottom: 8 }}>{rotulo(c)}</div>
                {catsDe(c).map((i, ix) => (
                  <div key={ix}
                    onClick={() => abrirDetalhe({ centro: c, categoria: i.categoria, titulo: `${rotulo(c)} · ${i.categoria}` })}
                    title="Clique para ver os lançamentos"
                    style={{
                    display: 'flex', justifyContent: 'space-between', gap: 10, cursor: 'pointer',
                    fontSize: 12, padding: '4px 0', borderBottom: `1px solid ${colors.border}33`,
                  }}>
                    <span style={{ color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {i.categoria} <span style={{ color: colors.textDim }}>({i.qtd})</span>
                    </span>
                    <strong style={{ color: colors.text, whiteSpace: 'nowrap' }}>{fmt(i.total)}</strong>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>);
      })()) : !sorted.length ? (
        <EmptyState message="Nada encontrado com esses filtros" icon={Layers} />
      ) : visao === 'faturas' ? (
        <Table>
          <thead><tr>
            <Th sortKey="mes_referencia" sortState={sortState} onSort={toggleSort}>Mês</Th>
            <Th sortKey="titular_nome" sortState={sortState} onSort={toggleSort}>Titular</Th>
            <Th sortKey="nome_cartao" sortState={sortState} onSort={toggleSort}>Cartão</Th>
            <Th>Banco</Th>
            <Th sortKey="data_vencimento" sortState={sortState} onSort={toggleSort}>Vencimento</Th>
            <Th sortKey="valor_total" sortState={sortState} onSort={toggleSort} align="right">Valor</Th>
            <Th align="center">Lançamentos</Th>
            <Th align="center">Status</Th>
          </tr></thead>
          <tbody>
            {sorted.map(f => (
              <TrHover key={f.id}>
                <Td>{f.mes_referencia}</Td>
                <Td style={{ color: colors.textMuted }}>{f.titular_nome || '-'}</Td>
                <Td style={{ fontWeight: 600 }}>{f.nome_cartao || '-'}{f.numeracao_final ? ` ···${f.numeracao_final}` : ''}</Td>
                <Td style={{ color: colors.textMuted }}>{f.banco_nome || '-'}</Td>
                <Td>{f.data_vencimento || '-'}</Td>
                <Td align="right" style={{ fontWeight: 700 }}>{fmt(f.valor_total)}</Td>
                <Td align="center" style={{ color: colors.textMuted, fontSize: 12 }}>
                  {f.ocr_processado ? `${f.conciliados}/${f.total_lancamentos}` : <span style={{ color: colors.warning }}>sem OCR</span>}
                </Td>
                <Td align="center"><Badge text={f.status} color={f.status === 'PAGO' ? colors.success : f.status === 'ATRASADO' ? colors.danger : colors.warning} /></Td>
              </TrHover>
            ))}
          </tbody>
        </Table>
      ) : (
        <Table>
          <thead><tr>
            <Th sortKey="data_compra" sortState={sortState} onSort={toggleSort}>Data</Th>
            <Th sortKey="titular_nome" sortState={sortState} onSort={toggleSort}>Titular</Th>
            <Th>Cartão</Th>
            <Th sortKey="descricao" sortState={sortState} onSort={toggleSort}>Descrição</Th>
            <Th sortKey="categoria" sortState={sortState} onSort={toggleSort}>Categoria</Th>
            <Th align="center">Origem</Th>
            <Th sortKey="valor" sortState={sortState} onSort={toggleSort} align="right">Valor</Th>
          </tr></thead>
          <tbody>
            {sorted.map(l => (
              <TrHover key={l.id}>
                <Td style={{ whiteSpace: 'nowrap' }}>{l.data_compra || l.data_vencimento || '-'}</Td>
                <Td style={{ color: colors.textMuted }}>{l.titular_nome || '-'}</Td>
                <Td style={{ color: colors.textMuted, maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.cartao_nome || l.nome_cartao || '-'}
                </Td>
                <Td style={{ maxWidth: 320 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.descricao}</div>
                  {l.fidelizado && (
                    <div style={{ fontSize: 10, color: colors.info, fontWeight: 600 }}>👤 {l.fidelizado}</div>
                  )}
                </Td>
                <Td style={{ color: colors.textMuted, fontSize: 12 }}>
                  {l.categoria || <span style={{ color: colors.textDim, fontStyle: 'italic' }}>sem categoria</span>}
                  {l.tipo_vinculo === 'PESSOAL' && (
                    <span style={{
                      marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                      background: `${colors.warning}25`, color: colors.warning,
                    }}>PESSOAL</span>
                  )}
                </Td>
                <Td align="center"><Badge text={l.origem} color={colors.textMuted} /></Td>
                <Td align="right" style={{ fontWeight: 700, color: Number(l.valor) < 0 ? colors.success : colors.text }}>
                  {fmt(l.valor)}
                </Td>
              </TrHover>
            ))}
          </tbody>
        </Table>
      )}

      {/* Detalhe de um centro (ou centro × categoria): as colunas que o gestor
          pediu pra conferir linha a linha — data, cartão, compra, parcela, valor. */}
      <Modal isOpen={!!detalhe} onClose={() => setDetalhe(null)}
        title={detalhe ? `Lançamentos — ${detalhe.titulo}` : ''} width={1080}>
        {detalheLoading ? <LoadingSpinner /> : !detalheData?.lancamentos?.length ? (
          <EmptyState message="Nenhum lançamento" icon={Wallet} />
        ) : (<>
          <div style={{
            display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
            marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${colors.border}`,
          }}>
            <div>
              <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600 }}>TOTAL</div>
              <div style={{ fontSize: 19, fontWeight: 800 }}>{fmt(detalheData.resumo?.total)}</div>
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>
              {detalheData.resumo?.qtd} lançamento(s)
              {detalheData.resumo?.truncado && ` — mostrando ${detalheData.resumo.exibindo}`}
            </div>
            <button onClick={() => baixarCsv(
              `fechamento-${detalhe.centro}${detalhe.categoria ? '-' + detalhe.categoria : ''}`,
              [
                { label: 'Data', get: l => l.data_compra || l.data_vencimento },
                { label: 'Mês', get: l => l.mes_referencia },
                { label: 'Titular', get: l => l.titular_nome },
                { label: 'Cartão', get: l => l.cartao_nome },
                { label: 'Compra', get: l => l.descricao },
                { label: 'Parcela', get: l => l.parcela || '' },
                { label: 'Categoria', get: l => l.categoria },
                { label: 'Operação', get: l => l.operacao },
                { label: 'Valor', get: l => l.valor },
              ], detalheData.lancamentos)}
              style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 8, border: `1px solid ${colors.info}55`,
                background: `${colors.info}18`, color: colors.info, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}>
              <Download size={13} /> CSV
            </button>
          </div>
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <Table>
              <thead><tr>
                <Th>Data</Th><Th>Cartão</Th><Th>Compra</Th>
                <Th align="center">Parcela</Th><Th align="right">Valor</Th>
              </tr></thead>
              <tbody>
                {detalheData.lancamentos.map(l => (
                  <TrHover key={l.id}>
                    <Td style={{ whiteSpace: 'nowrap' }}>{l.data_compra || l.data_vencimento || '-'}</Td>
                    <Td style={{ color: colors.textMuted, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.cartao_nome || '-'}{l.numeracao_final ? ` ···${l.numeracao_final}` : ''}
                    </Td>
                    <Td style={{ maxWidth: 380 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.descricao}</div>
                      <div style={{ fontSize: 10, color: colors.textDim }}>
                        {l.categoria || 'sem categoria'}
                        {l.centro_fechamento_manual ? ' · ajustado à mão' : ''}
                      </div>
                    </Td>
                    <Td align="center" style={{ color: colors.textMuted, fontSize: 12, whiteSpace: 'nowrap' }}>
                      {l.parcela || '—'}
                    </Td>
                    <Td align="right" style={{ fontWeight: 700, color: Number(l.valor) < 0 ? colors.success : colors.text }}>
                      {fmt(l.valor)}
                    </Td>
                  </TrHover>
                ))}
              </tbody>
            </Table>
          </div>
        </>)}
      </Modal>

      {R.truncado && (
        <div style={{ marginTop: 10, fontSize: 12, color: colors.warning }}>
          Mostrando {R.exibindo} de {R.qtd} lançamentos. Os totais e a quebra por categoria acima
          consideram <strong>todos</strong> os {R.qtd} — só a lista está cortada. Aperte o período
          ou os filtros para ver o resto.
        </div>
      )}
    </div>
  );
}
